import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { MessageService } from "@openng/optimus-ui/api";
import { map, Observable, tap } from "rxjs";

import { cloneDeep } from "lodash";
import { environment } from "../../../../environments/environment";
import { ApiErrorHandlerService } from "../../utils/api-error-handler/api-error-handler.service";
import { Instruction, Definition, RuleChain } from "../../../models/api/ruleChain";

@Injectable({
  providedIn: "root",
})
export class ButlerService {
  private http = inject(HttpClient);
  private apiErrorHandler = inject(ApiErrorHandlerService);
  private message = inject(MessageService);
  private translate = inject(TranslateService);

  /**
   * @description Edit an instruction
   * @param instruction The instruction to edit
   * @returns An Observable of the edited instruction
   */
  public editInstruction(instruction: Instruction) {
    const instructionValues = cloneDeep(instruction);
    instructionValues.nodes.forEach((node) => {
      if (node?.definition?.id) {
        if (node.definition?.type === "Input" || node.definition?.type === "Timer") {
          node.value = true;
        }

        node.definition = { id: node.definition.id };
      }

      node.position = Number(node.position);
      if (node.value === null) {
        node.value = true;
      }
    });

    const instructionId = instruction.id;
    delete instructionValues.id;
    return this.http
      .put(environment.baseUrl + `/instruction/${instructionId}`, instructionValues, {
        observe: "response",
      })
      .pipe(
        tap(() => {
          this.message.add({
            key: "global",
            severity: "success",
            summary: this.translate.instant("common.success"),
            detail: this.translate.instant(`success_messages.edit`, {
              object: this.translate.instant("common.entities.instruction"),
            }),
            life: 4500,
          });
        }),
        this.apiErrorHandler.handle("instruction")
      );
  }

  /**
   * @description Create a new instruction
   * @param instruction The instruction to create
   * @returns An Observable of the created instruction
   */
  public createInstruction(instruction: Instruction) {
    return this.http
      .post(environment.baseUrl + `/instruction`, instruction, {
        observe: "response",
      })
      .pipe(
        tap(() => {
          this.message.add({
            key: "global",
            severity: "success",
            summary: this.translate.instant("common.success"),
            detail: this.translate.instant(`success_messages.add`, {
              object: this.translate.instant("common.entities.instruction"),
            }),
            life: 4500,
          });
        }),
        this.apiErrorHandler.handle("instruction")
      );
  }

  /**
   * @description Create a new definition
   * @param definition The definition to create
   * @returns An Observable of the created definition
   */
  public createDefinition(definition: Definition) {
    const definitionValues = cloneDeep(definition);
    if (definition.type === "Timer" && typeof definitionValues.value === "number") {
      definitionValues.value = Math.round(definitionValues.value * 1000);
    }
    if (definition.type === "Input" || definition.type === "Relais") {
      delete definitionValues.gateSystem?.label;
    } else {
      delete definitionValues.gateSystem;
    }
    return this.http
      .post(environment.baseUrl + `/definition`, definitionValues, {
        observe: "response",
      })
      .pipe(
        tap(() => {
          this.message.add({
            key: "global",
            severity: "success",
            summary: this.translate.instant("common.success"),
            detail: this.translate.instant(`success_messages.add`, {
              object: this.translate.instant("common.entities.definition"),
            }),
            life: 4500,
          });
        }),
        this.apiErrorHandler.handle("definition")
      );
  }

  /**
   * @description Edit a definition
   * @param definition The definition to edit
   * @returns An Observable of the edited definition
   */
  public editDefinition(definition: Definition) {
    const definitionValues = cloneDeep(definition);
    const definitionId = definition.id;

    delete definitionValues.id;
    if (definition.type === "Timer" && typeof definitionValues.value === "number") {
      definitionValues.value = Math.round(definitionValues.value * 1000);
    }
    if (definition.type === "Input" || definition.type === "Relais") {
      delete definitionValues.gateSystem?.label;
    } else {
      delete definitionValues.gateSystem;
    }
    return this.http
      .put(environment.baseUrl + `/definition/${definitionId}`, definitionValues, {
        observe: "response",
      })
      .pipe(
        tap(() => {
          this.message.add({
            key: "global",
            severity: "success",
            summary: this.translate.instant("common.success"),
            detail: this.translate.instant(`success_messages.edit`, {
              object: this.translate.instant("common.entities.definition"),
            }),
            life: 4500,
          });
        }),
        this.apiErrorHandler.handle("definition")
      );
  }

  /**
   * @description Delete a definition
   * @param definitionId The id of the definition to delete
   * @returns An Observable of the response
   */
  public deleteDefinition(definitionId: string) {
    return this.http.delete(environment.baseUrl + `/definition/${definitionId}`).pipe(
      tap(() => {
        this.message.add({
          key: "global",
          severity: "success",
          summary: this.translate.instant("common.success"),
          detail: this.translate.instant(`success_messages.delete`, {
            object: this.translate.instant("common.entities.definition"),
          }),
          life: 4500,
        });
      }),
      this.apiErrorHandler.handle("definition")
    );
  }

  /**
   * @description Get all rulechains
   * @returns An Observable of an array of rulechains
   */
  public getRuleChains() {
    return this.http.get<RuleChain[]>(environment.baseUrl + `/ruleChain`).pipe(
      this.apiErrorHandler.handle("rule_chain"),
      map((res) => {
        const ruleChains = res;

        return ruleChains.sort((a: RuleChain, b: RuleChain) => {
          if (a.id && b.id) {
            return Number(a.id) > Number(b.id) ? 1 : -1;
          } else {
            return 0;
          }
        });
      })
    ) as Observable<RuleChain[]>;
  }

  /**
   * @description Create a new rulechain
   * @param ruleChain The rulechain to create
   * @returns An Observable of the created rulechain
   */
  public createRuleChain(ruleChain: RuleChain) {
    return this.http
      .post<RuleChain>(environment.baseUrl + `/ruleChain`, ruleChain, {
        observe: "response",
      })
      .pipe(
        tap(() => {
          this.message.add({
            key: "global",
            severity: "success",
            summary: this.translate.instant("common.success"),
            detail: this.translate.instant(`success_messages.add`, {
              object: this.translate.instant("common.entities.rule_chain"),
            }),
            life: 4500,
          });
        }),
        this.apiErrorHandler.handle("rule_chain")
      );
  }

  /**
   * @description Edit a rulechain
   * @param ruleChain The rulechain to edit
   * @returns An Observable of the edited rulechain
   */
  public editRuleChain(ruleChain: RuleChain) {
    const ruleChainId = ruleChain.id;
    delete ruleChain.id;
    return this.http
      .put(environment.baseUrl + `/ruleChain/${ruleChainId}`, ruleChain, {
        observe: "response",
      })
      .pipe(
        tap(() => {
          this.message.add({
            key: "global",
            severity: "success",
            summary: this.translate.instant("common.success"),
            detail: this.translate.instant(`success_messages.edit`, {
              object: this.translate.instant("common.entities.rule_chain"),
            }),
            life: 4500,
          });
        }),
        this.apiErrorHandler.handle("rule_chain")
      );
  }

  /**
   * @description Delete a rulechain
   * @param ruleChainId The id of the rulechain to delete
   * @returns An Observable of the response
   */
  public deleteRuleChain(ruleChainId: string) {
    return this.http
      .delete(environment.baseUrl + `/ruleChain/${ruleChainId}`, {
        observe: "response",
      })
      .pipe(
        tap(() => {
          this.message.add({
            key: "global",
            severity: "success",
            summary: this.translate.instant("common.success"),
            detail: this.translate.instant(`success_messages.delete`, {
              object: this.translate.instant("common.entities.rule_chain"),
            }),
            life: 4500,
          });
        }),
        this.apiErrorHandler.handle("rule_chain")
      );
  }

  /**
   * @description Get the details of a rulechain
   * @param ruleChainId The id of the rulechain to get the details for
   * @returns An Observable of the rulechain
   */
  public getRuleChainDetails(ruleChainId: string) {
    return this.http.get<RuleChain>(environment.baseUrl + `/ruleChain/${ruleChainId}`).pipe(
      this.apiErrorHandler.handle("rule_chain"),
      map((res) => {
        res.definitions?.forEach((d) => {
          if (d.type === "Timer" && typeof d.value === "number") {
            d.value = d.value / 1000;
          }
        });
        res.definitions?.sort((a, b) => Number(a.id) - Number(b.id));
        if (res?.instructions) {
          res?.instructions.forEach((instruction) => {
            instruction.nodes.forEach((node) => {
              node.position = Number(node.position);
            });
            instruction.nodes.sort((a, b) => {
              if (a.position !== undefined && b.position !== undefined) {
                return a.position - b.position;
              } else {
                return 0;
              }
            });
          });
        }
        return res;
      })
    ) as Observable<RuleChain>;
  }
}
